
import React, { useEffect, useState } from "react";
import axios from "axios";
import { getAPIUrl } from "../../api/apiUtils";
import { useRegionsData } from "../../hooks/useRegions";
import { useGlobalFilters, useGlobalFiltersForAPI, useGlobalFiltersSubscription } from '../../hooks/useGlobalFilters';
import { useVKUsers } from "../../hooks/useVKUsers";
import Pagination from "../../components/Pagination";
import ExportModal from "../../components/ExportModal";
import { groupIdToTitle } from "../../utils/groupTitles";
import "../CentralitiesTable.scss";

// Добавляем стили для анимации
const spinnerStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Добавляем стили в head если их еще нет
if (!document.querySelector('#spinner-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'spinner-styles';
  styleSheet.textContent = spinnerStyles;
  document.head.appendChild(styleSheet);
}

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];

// Колонки для таблицы рейтинга
const ratingColumns = [
  { key: "avatar", label: "Фото" },
  { key: "ranking_position", label: "Место в рейтинге" },
  { key: "user_id", label: "ID пользователя" },
  { key: "nickname", label: "Ник" },
  { key: "final_index", label: "Рейтинг инфлюенсера" },
  { key: "total_comments", label: "Комментарии" },
  { key: "total_likes", label: "Лайки" },
  { key: "total_likes_received", label: "Полученные лайки" },
  { key: "unique_posts_commented", label: "Уникальные посты (коммент.)" },
  { key: "unique_posts_liked", label: "Уникальные посты (лайки)" },
];

// Колонки для модального окна с характеристиками
const modalColumns = [
  { key: "user_id", label: "Пользователь" },
  { key: "group_id", label: "Группа" },
  { key: "degree_centrality", label: "Степень" },
  { key: "degree_edges", label: "Связи" },
  { key: "degree_normalized", label: "Норм. степень" },
  { key: "betweenness_centrality", label: "Опосредованность" },
  { key: "betweenness_edges", label: "Связи опосред." },
  { key: "betweenness_normalized", label: "Норм. опосред." },
  { key: "closeness_centrality", label: "Близость" },
  { key: "closeness_edges", label: "Связи близости" },
  { key: "closeness_normalized", label: "Норм. близость" },
  { key: "eigenvector_centrality", label: "Собственный вектор" },
  { key: "eigenvector_edges", label: "Связи собств. вектора" },
  { key: "eigenvector_normalized", label: "Норм. собств. вектор" },
];

const CsvTable = ({ data: propData = [] }) => {
  const { regions, isLoading: loadingGroups, error: groupsError } = useRegionsData();
  const { fetchUsers, getUserInfo, isUserLoading } = useVKUsers();
  const filtersStore = useGlobalFilters();
  const apiFilters = useGlobalFiltersForAPI();
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [sortField, setSortField] = useState("final_index");
  const [sortOrder, setSortOrder] = useState("desc");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [userDetails, setUserDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(new Set());
  const [imageModal, setImageModal] = useState({ show: false, src: '', alt: '' });
  const [showExportModal, setShowExportModal] = useState(false);
  // Используем современный подход с MobX реактивностью
  const [globalFilters, setGlobalFilters] = useState(filtersStore.filters);
  
  // Подписываемся на изменения фильтров через MobX
  useGlobalFiltersSubscription((filters) => {
    setGlobalFilters(filters);
  });

  const selectedGroups = globalFilters.selectedGroups || [];

  useEffect(() => {
    if (propData.length > 0) setData(propData);
  }, [propData]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (selectedGroups.length === 0) {
        setData([]);
        setTotal(0);
        return;
      }

      const groupIdsString = selectedGroups.join(',');

      const resTop = await axios.get(`${getAPIUrl()}/influencers/top_final_index`, {
        params: {
          start_date: globalFilters.startDate,
          end_date: globalFilters.endDate,
          group_ids: groupIdsString,
          limit: 999999, // Загружаем все данные для отображения в таблице
        },
      });

      const resActivity = await axios.get(`${getAPIUrl()}/influencers/activity`, {
        params: {
          start_date: globalFilters.startDate,
          end_date: globalFilters.endDate,
          group_ids: groupIdsString,
          limit: 999999, // Загружаем все данные для отображения в таблице
        },
      });

      const topData = Array.isArray(resTop.data) ? resTop.data : [];
      const activityData = Array.isArray(resActivity.data) ? resActivity.data : [];

      const mergedData = topData.map((topItem) => {
        const activityItem = activityData.find((act) => act.user_id === topItem.user_id) || {};
        return {
          ...topItem,
          total_comments: activityItem.total_comments || 0,
          total_likes: activityItem.total_likes || 0,
          total_likes_received: activityItem.total_likes_received || 0,
          unique_posts_commented: activityItem.unique_posts_commented || 0,
          unique_posts_liked: activityItem.unique_posts_liked || 0,
        };
      });

      // Сначала сортируем по final_index для определения мест в рейтинге
      const rankingData = [...mergedData].sort((a, b) => b.final_index - a.final_index);
      
      // Добавляем места в рейтинге на основе final_index (сортировка по убыванию)
      const dataWithRanking = rankingData.map((item, index) => ({
        ...item,
        ranking_position: index + 1
      }));

      // Теперь применяем пользовательскую сортировку
      const sortedData = [...dataWithRanking].sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
        }

        const aStr = String(aVal || '').toLowerCase();
        const bStr = String(bVal || '').toLowerCase();

        if (sortOrder === 'asc') {
          return aStr.localeCompare(bStr);
        } else {
          return bStr.localeCompare(aStr);
        }
      });

      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const pagedData = sortedData.slice(startIndex, endIndex);

      setData(pagedData);
      setTotal(sortedData.length);

      const currentPageUserIds = pagedData.map((item) => item.user_id).filter(Boolean);
      if (currentPageUserIds.length > 0) {
        fetchUsers(currentPageUserIds);
      }
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [globalFilters, sortField, sortOrder, page, pageSize]);

  const handleSort = (key) => {
    if (sortField === key) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(key);
      setSortOrder(key === "final_index" ? "desc" : "asc");
    }
    setPage(1);
  };

  const openImageModal = (src, alt) => {
    setImageModal({ show: true, src, alt });
  };

  const closeImageModal = () => {
    setImageModal({ show: false, src: '', alt: '' });
  };

  const toggleRowExpansion = async (user) => {
    const userId = user.user_id;
    const newExpandedRows = new Set(expandedRows);
    
    if (expandedRows.has(userId)) {
      newExpandedRows.delete(userId);
      setExpandedRows(newExpandedRows);
    } else {
      newExpandedRows.add(userId);
      setExpandedRows(newExpandedRows);
      
      if (!userDetails[userId]) {
        const newLoadingDetails = new Set(loadingDetails);
        newLoadingDetails.add(userId);
        setLoadingDetails(newLoadingDetails);
        
        try {
          const res = await axios.get(`${getAPIUrl()}/influencers/centralities`, {
            params: {
              group_ids: selectedGroups.join(','),
              user_id: userId,
              limit: 1000,
              start_date: globalFilters.startDate,
              end_date: globalFilters.endDate
            },
          });
          
          const userCentralities = Array.isArray(res.data?.data) ? res.data.data : [];
          const filteredData = userCentralities.filter(item => String(item.user_id) === String(userId));
          
          setUserDetails(prev => ({
            ...prev,
            [userId]: filteredData
          }));
        } catch (error) {
          console.error('Ошибка загрузки характеристик пользователя:', error);
          setUserDetails(prev => ({
            ...prev,
            [userId]: []
          }));
        } finally {
          const newLoadingDetailsAfter = new Set(loadingDetails);
          newLoadingDetailsAfter.delete(userId);
          setLoadingDetails(newLoadingDetailsAfter);
        }
      }
    }
  };

  // Функция для загрузки всех данных без пагинации (для локального экспорта - резервная)
  const fetchAllData = async () => {
    try {
      if (selectedGroups.length === 0) {
        return [];
      }

      const groupIdsString = selectedGroups.join(',');

      const resTop = await axios.get(`${getAPIUrl()}/influencers/top_final_index`, {
        params: {
          start_date: globalFilters.startDate,
          end_date: globalFilters.endDate,
          group_ids: groupIdsString,
          limit: 999999, // Получаем все данные без ограничений
        },
      });

      const resActivity = await axios.get(`${getAPIUrl()}/influencers/activity`, {
        params: {
          start_date: globalFilters.startDate,
          end_date: globalFilters.endDate,
          group_ids: groupIdsString,
          limit: 999999, // Получаем все данные без ограничений
        },
      });

      const topData = Array.isArray(resTop.data) ? resTop.data : [];
      const activityData = Array.isArray(resActivity.data) ? resActivity.data : [];

      const mergedData = topData.map((topItem) => {
        const activityItem = activityData.find((act) => act.user_id === topItem.user_id) || {};
        return {
          ...topItem,
          total_comments: activityItem.total_comments || 0,
          total_likes: activityItem.total_likes || 0,
          total_likes_received: activityItem.total_likes_received || 0,
          unique_posts_commented: activityItem.unique_posts_commented || 0,
          unique_posts_liked: activityItem.unique_posts_liked || 0,
        };
      });

      // Сортируем по final_index для определения мест в рейтинге
      const rankingData = [...mergedData].sort((a, b) => b.final_index - a.final_index);
      
      // Добавляем места в рейтинге
      const dataWithRanking = rankingData.map((item, index) => ({
        ...item,
        ranking_position: index + 1
      }));

      return dataWithRanking;
    } catch (error) {
      console.error('Ошибка загрузки всех данных:', error);
      return [];
    }
  };


  return (
    <div style={{ background: "#f5f6fa", borderRadius: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.06)", minHeight: 600 }}>
      {/* Модальное окно экспорта */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        dataType="influencers"
        title="Экспорт инфлюенсеров"
      />
      
      <main style={{ padding: 32, background: "#fff", borderRadius: 16, minHeight: 600 }}>
        <div style={{ borderRadius: 12, border: "1px solid #e0e0e0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead style={{ background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)", position: "sticky", top: 0, zIndex: 1, boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
              <tr>
                {ratingColumns.map(col => (
                  <th
                    key={col.key}
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e0e0e0",
                      fontWeight: 600,
                      cursor: col.key !== "avatar" ? "pointer" : "default",
                      background: sortField === col.key ? "#e3e7f1" : undefined,
                      color: sortField === col.key ? "#1976d2" : undefined,
                      userSelect: "none",
                      textAlign: col.key === "avatar" ? "center" : "left"
                    }}
                    onClick={() => col.key !== "avatar" && handleSort(col.key)}
                  >
                    {col.label}
                    {sortField === col.key && (
                      <span style={{ marginLeft: 4 }}>{sortOrder === "asc" ? "▲" : "▼"}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {selectedGroups.length === 0 ? (
                <tr><td colSpan={ratingColumns.length} style={{ textAlign: "center", padding: 24 }}>Нет выбранной группы</td></tr>
              ) : loading ? (
                <tr><td colSpan={ratingColumns.length} style={{ textAlign: "center", padding: 24 }}>Загрузка...</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={ratingColumns.length} style={{ textAlign: "center", padding: 24 }}>Нет данных</td></tr>
              ) : (
                data.map((row, i) => (
                  <React.Fragment key={row.user_id}>
                    <tr 
                      style={{ 
                        background: i % 2 === 0 ? "#fafbfc" : "#fff",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        borderLeft: "3px solid transparent"
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = "#e8f4fd";
                        e.currentTarget.style.borderLeft = "3px solid #1976d2";
                        e.currentTarget.style.transform = "translateX(2px)";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = i % 2 === 0 ? "#fafbfc" : "#fff";
                        e.currentTarget.style.borderLeft = "3px solid transparent";
                        e.currentTarget.style.transform = "translateX(0)";
                      }}
                      onClick={() => toggleRowExpansion(row)}
                    >
                      {ratingColumns.map((col, colIndex) => (
                        <td key={col.key} style={{ 
                          padding: "6px 12px", 
                          borderBottom: expandedRows.has(row.user_id) ? "none" : "1px solid #f0f0f0",
                          textAlign: col.key === "avatar" ? "center" : "left",
                          position: "relative"
                        }}>
                          {colIndex === 0 && (
                            <div style={{
                              position: "absolute",
                              left: 8,
                              top: "50%",
                              transform: "translateY(-50%)",
                              width: 18,
                              height: 18,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "4px",
                              background: expandedRows.has(row.user_id) ? "#1976d2" : "#e2e8f0",
                              color: expandedRows.has(row.user_id) ? "#fff" : "#666",
                              fontSize: 10,
                              fontWeight: "bold",
                              transition: "all 0.2s ease",
                              cursor: "pointer",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                            }}>
                              {expandedRows.has(row.user_id) ? "−" : "+"}
                            </div>
                          )}
                          {col.key === "avatar" ? (
                            <div style={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              background: "#e0e0e0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              margin: "0 auto",
                              fontSize: 16,
                              color: "#666",
                              overflow: "hidden",
                              cursor: "pointer"
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const userInfo = getUserInfo(row.user_id);
                              if (userInfo?.photo_200) {
                                openImageModal(userInfo.photo_200, `Аватар пользователя ${userInfo.full_name}`);
                              }
                            }}>
                              {(() => {
                                const userInfo = getUserInfo(row.user_id);
                                const isLoading = isUserLoading(row.user_id);
                                
                                if (isLoading) {
                                  return <div style={{ fontSize: 12, color: "#999" }}>...</div>;
                                }
                                
                                if (userInfo?.photo_200) {
                                  return (
                                    <img 
                                      src={userInfo.photo_200} 
                                      alt="Аватар" 
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        transition: "transform 0.2s ease"
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                      onMouseOver={(e) => e.target.style.transform = "scale(1.1)"}
                                      onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                                    />
                                  );
                                }
                                
                                return "👤";
                              })()}
                              <div style={{
                                display: "none",
                                width: "100%",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 18,
                                color: "#666"
                              }}>
                                👤
                              </div>
                            </div>
                          ) : col.key === "user_id" ? (
                            <a
                              href={(() => {
                                const userInfo = getUserInfo(row.user_id);
                                if (row.vk_link) return row.vk_link;
                                if (userInfo?.screen_name && userInfo.screen_name !== `id${row.user_id}`) {
                                  return `https://vk.com/${userInfo.screen_name}`;
                                }
                                return `https://vk.com/id${row.user_id}`;
                              })()}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ 
                                color: "#1976d2", 
                                textDecoration: "underline",
                                fontWeight: 600
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {row.user_id}
                            </a>
                          ) : col.key === "nickname" ? (
                            <span style={{ fontStyle: "italic", color: "#666" }}>
                              {(() => {
                                const userInfo = getUserInfo(row.user_id);
                                const isLoading = isUserLoading(row.user_id);
                                
                                if (isLoading) {
                                  return "Загрузка...";
                                }
                                
                                if (userInfo?.full_name && userInfo.full_name !== `Пользователь ${row.user_id}`) {
                                  return userInfo.full_name;
                                }
                                
                                if (userInfo?.screen_name && userInfo.screen_name !== `id${row.user_id}`) {
                                  return `@${userInfo.screen_name}`;
                                }
                                
                                return `Пользователь_${row.user_id}`;
                              })()}
                            </span>
                          ) : col.key === "ranking_position" ? (
                            <span style={{ fontWeight: 600, color: "#1976d2", fontSize: "16px" }}>
                              {row.ranking_position}-е
                            </span>
                          ) : col.key === "final_index" ? ( 
                            <span style={{ fontWeight: 500, color: "#374151" }}>
                              {typeof row[col.key] === "number" ? row[col.key].toFixed(4) : row[col.key]}
                            </span>
                          ) : ["total_comments", "total_likes", "total_likes_received", "unique_posts_commented", "unique_posts_liked"].includes(col.key) ? (
                            <span style={{ fontWeight: 500, color: "#374151" }}>
                              {row[col.key].toLocaleString()}
                            </span>
                          ) : (
                            typeof row[col.key] === "number" ? row[col.key].toFixed(4) : row[col.key]
                          )}
                        </td>
                      ))}
                    </tr>
                    {expandedRows.has(row.user_id) && (
                      <tr style={{ background: "#f8fafc" }}>
                        <td colSpan={ratingColumns.length} style={{ 
                          padding: 0,
                          borderBottom: "1px solid #f0f0f0"
                        }}>
                          <div style={{ padding: 20 }}>
                            {loadingDetails.has(row.user_id) ? (
                              <div style={{ textAlign: "center", color: "#666", padding: 20 }}>
                                Загрузка характеристик...
                              </div>
                            ) : userDetails[row.user_id] && userDetails[row.user_id].length > 0 ? (
                              <div style={{ padding: "16px 24px" }}>
                                <div style={{
                                  background: "#fff",
                                  borderRadius: 12,
                                  border: "1px solid #e0e0e0",
                                  overflow: "hidden",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
                                }}>
                                  <div style={{ 
                                    background: "#f8fafc", 
                                    padding: "12px 16px", 
                                    borderBottom: "1px solid #e0e0e0" 
                                  }}>
                                    <h4 style={{ 
                                      margin: 0, 
                                      color: "#1976d2", 
                                      fontSize: 16,
                                      fontWeight: 600
                                    }}>
                                      Характеристики центральностей
                                    </h4>
                                  </div>
                                  <table style={{ 
                                    width: "100%", 
                                    borderCollapse: "collapse", 
                                    fontSize: 13 
                                  }}>
                                    <thead style={{ background: "#f5f7fa" }}>
                                      <tr>
                                        {modalColumns.map(col => (
                                          <th key={col.key} style={{
                                            padding: "10px 12px",
                                            borderBottom: "1px solid #e0e0e0",
                                            fontWeight: 600,
                                            textAlign: "left",
                                            color: "#374151",
                                            fontSize: 12
                                          }}>
                                            {col.label}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {userDetails[row.user_id].map((detail, detailIndex) => (
                                        <tr key={detailIndex} style={{ 
                                          background: detailIndex % 2 === 0 ? "#fafbfc" : "#fff",
                                          transition: "background-color 0.2s"
                                        }}>
                                          {modalColumns.map(col => (
                                            <td key={col.key} style={{ 
                                              padding: "10px 12px", 
                                              borderBottom: "1px solid #f0f2f5",
                                              color: "#374151"
                                            }}>
                                              {col.key === "group_id"
                                                ? (groupIdToTitle[String(detail[col.key])] ||
                                                   regions.find(r => String(r.group_id) === String(detail[col.key]))?.title ||
                                                   detail[col.key])
                                                : col.key === "user_id"
                                                  ? detail[col.key]
                                                  : (typeof detail[col.key] === "number"
                                                      ? detail[col.key].toFixed(4)
                                                      : detail[col.key])}
                                            </td>
                                          ))}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            ) : (
                              <div style={{ textAlign: "center", color: "#666", padding: 20 }}>
                                Нет данных о характеристиках пользователя
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontWeight: 600, color: "#1976d2", fontSize: 16 }}>
                Рейтинг инфлюенсеров
              </span>
              <span style={{ fontWeight: 500, color: "#6b7280", fontSize: 14 }}>
                Показано {total > 0 ? ((page - 1) * pageSize) + 1 : 0}-{Math.min(page * pageSize, total)} из {total} записей
              </span>
            </div>
            <select 
              value={pageSize} 
              onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #4a90e2",
                background: "#fff",
                fontSize: 14,
                color: "#374151",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.2s ease",
                outline: "none"
              }}
              onFocus={(e) => e.target.style.borderColor = "#357abd"}
              onBlur={(e) => e.target.style.borderColor = "#4a90e2"}
            >
              {PAGE_SIZE_OPTIONS.map(size => (
                <option key={size} value={size}>Показать {size}</option>
              ))}
            </select>
          </div>
          <Pagination
            currentPage={page}
            totalPages={Math.ceil(total / pageSize) || 1}
            onPageChange={(newPage) => setPage(newPage)}
            maxVisiblePages={5}
          />
          <button 
            className="analysis-members-export-btn" 
            onClick={() => setShowExportModal(true)}
            disabled={total === 0 || selectedGroups.length === 0}            
            style={{
              padding: "10px 14px",
              background: (total === 0 || selectedGroups.length === 0) ? "#f4f6f8" : "#f4f6f8",
              color: (total === 0 || selectedGroups.length === 0) ? "#999" : "#222",
              borderColor: "#d1d5db",
              borderRadius: 8,
              cursor: (total === 0 || selectedGroups.length === 0) ? "not-allowed" : "pointer",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 15,
              fontWeight: 500,
              
            }}
            onMouseOver={(e) => {
              if (total > 0 && selectedGroups.length > 0) {
                e.currentTarget.style.background = "#e5e7eb";                
                e.currentTarget.style.boxShadow = "0 4px 16px #3c3c3c3a";
              }
            }}
            onMouseOut={(e) => {
              if (total > 0 && selectedGroups.length > 0) {
                e.currentTarget.style.background = "#f4f6f8";
                e.currentTarget.style.boxShadow = "0 0px 0px #3c3c3c3a";
              }
            }}
          >
            Экспорт данных
          </button>
        </div>
      </main>
      {imageModal.show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            cursor: "pointer"
          }}
          onClick={closeImageModal}
        >
          <div
            style={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
              background: "#fff",
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "0 10px 30px rgba(0,0,0,0.3)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeImageModal}
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                background: "rgba(0,0,0,0.7)",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                width: 30,
                height: 30,
                cursor: "pointer",
                fontSize: 18,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1001
              }}
            >
              ×
            </button>
            <img
              src={imageModal.src}
              alt={imageModal.alt}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                display: "block"
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CsvTable;
